/**
 * IELTS Listening Exam — real-IELTS-style split-screen.
 *
 *   ┌──────────────────────────────┬──────────────────────────────┐
 *   │  AUDIO + TRANSCRIPT (left)   │  QUESTIONS (right)           │
 *   │  - audio player              │  - grouped IELTS instructions│
 *   │  - section context           │  - 12 listening question     │
 *   │  - transcript                │    types                     │
 *   │  - optional map/diagram      │                              │
 *   └──────────────────────────────┴──────────────────────────────┘
 *
 * Mobile: stacks vertically, audio + transcript first, questions below.
 */
import React, { useMemo, useRef, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Check, X, Volume2, Play, Pause, RotateCcw, FileText, EyeOff, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { EXAM_TEXT_CLS } from '@/components/exam/QuestionDisplay';

const QTYPE_LABEL = {
  form_completion: 'Form Completion',
  note_completion: 'Note Completion',
  table_completion: 'Table Completion',
  flow_chart_completion: 'Flow-chart Completion',
  sentence_completion: 'Sentence Completion',
  summary_completion: 'Summary Completion',
  map_labeling: 'Map Labeling',
  diagram_labeling: 'Diagram Labeling',
  multiple_choice_one_answer: 'Multiple Choice',
  multiple_choice_multiple_answers: 'Multiple Choice (Multi)',
  matching: 'Matching',
  short_answer: 'Short Answer',
};

const RADIO_LETTER_TYPES = new Set(['multiple_choice_one_answer', 'matching']);
const TEXT_TYPES = new Set([
  'form_completion', 'note_completion', 'table_completion', 'flow_chart_completion',
  'sentence_completion', 'summary_completion', 'map_labeling', 'diagram_labeling',
  'short_answer',
]);

function groupQuestions(questions) {
  const groups = [];
  let cur = null;
  for (const q of questions) {
    const key = `${q.question_type}::${q.instructions || ''}`;
    if (!cur || cur.key !== key) {
      cur = { key, question_type: q.question_type, instructions: q.instructions || '', items: [] };
      groups.push(cur);
    }
    cur.items.push(q);
  }
  return groups;
}

const stateClasses = (selected, result, isCorrectAnswer, userPicked) => {
  if (result) {
    if (isCorrectAnswer) return 'border-green-500 bg-green-50';
    if (userPicked && !isCorrectAnswer) return 'border-red-500 bg-red-50';
    return 'border-gray-200 bg-white';
  }
  if (selected) return 'border-[#007A7A] bg-[#E6F4F4] shadow-sm';
  return 'border-gray-200 bg-white hover:border-gray-400 hover:bg-gray-50';
};

const McqRow = ({ letter, text, selected, locked, result, isCorrect, userPicked, onPick, testId }) => (
  <button type="button"
    onClick={locked ? undefined : () => onPick(letter)}
    disabled={locked}
    data-testid={testId}
    className={`w-full text-left rounded-lg border-2 px-3 py-2.5 transition-all flex items-start gap-3 ${stateClasses(selected, result, isCorrect, userPicked)} ${locked ? 'cursor-default' : 'cursor-pointer'}`}>
    <span className={`flex-shrink-0 w-6 h-6 mt-0.5 rounded-full border flex items-center justify-center text-[11px] font-bold uppercase ${
      result && isCorrect ? 'bg-green-500 border-green-500 text-white' :
      result && userPicked ? 'bg-red-500 border-red-500 text-white' :
      selected ? 'bg-[#007A7A] border-[#007A7A] text-white' :
      'bg-white border-gray-300 text-gray-700'
    }`}>{letter.toUpperCase()}</span>
    <span className={`flex-1 min-w-0 text-sm text-gray-900 ${EXAM_TEXT_CLS}`}>{text}</span>
    {result && isCorrect && <Check className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />}
    {result && userPicked && !isCorrect && <X className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />}
  </button>
);

const QuestionItem = ({ q, value, locked, perResult, onChange }) => {
  const qt = q.question_type;
  const correct = perResult?.correct_answer;

  if (RADIO_LETTER_TYPES.has(qt)) {
    const opts = ['a', 'b', 'c', 'd', 'e']
      .map((l) => [l, q[`option_${l}`]])
      .filter(([, t]) => t && t.length > 0);
    return (
      <div className="space-y-1.5">
        {opts.map(([letter, text]) => {
          const isCorrect = !!perResult && correct && letter === correct.toLowerCase();
          const userPicked = value === letter;
          return (
            <McqRow key={letter}
              letter={letter} text={text}
              selected={!perResult && userPicked}
              locked={locked} result={perResult}
              isCorrect={isCorrect} userPicked={userPicked}
              onPick={(l) => onChange(q.id, { selected_option: l })}
              testId={`ielts-ls-q${q.question_number}-opt-${letter}`} />
          );
        })}
      </div>
    );
  }

  if (qt === 'multiple_choice_multiple_answers') {
    const opts = ['a', 'b', 'c', 'd', 'e']
      .map((l) => [l, q[`option_${l}`]])
      .filter(([, t]) => t && t.length > 0);
    const picked = new Set((value || '').split(',').map((x) => x.trim().toLowerCase()).filter(Boolean));
    const correctSet = correct ? new Set(correct.split(',').map((x) => x.trim().toLowerCase()).filter(Boolean)) : null;
    return (
      <div className="space-y-1.5">
        {opts.map(([letter, text]) => {
          const isSelected = picked.has(letter);
          const isCorrect = correctSet?.has(letter);
          const userPicked = isSelected;
          const toggle = () => {
            const next = new Set(picked);
            if (next.has(letter)) next.delete(letter); else next.add(letter);
            onChange(q.id, { text_answer: Array.from(next).join(',') });
          };
          return (
            <button key={letter} type="button"
              onClick={locked ? undefined : toggle} disabled={locked}
              data-testid={`ielts-ls-q${q.question_number}-opt-${letter}`}
              className={`w-full text-left rounded-lg border-2 px-3 py-2.5 transition-all flex items-start gap-3 ${stateClasses(isSelected, perResult, isCorrect, userPicked)} ${locked ? 'cursor-default' : 'cursor-pointer'}`}>
              <span className={`flex-shrink-0 w-6 h-6 mt-0.5 rounded border flex items-center justify-center text-[11px] font-bold uppercase ${
                perResult && isCorrect ? 'bg-green-500 border-green-500 text-white' :
                perResult && userPicked ? 'bg-red-500 border-red-500 text-white' :
                isSelected ? 'bg-[#007A7A] border-[#007A7A] text-white' :
                'bg-white border-gray-300 text-gray-700'
              }`}>{letter.toUpperCase()}</span>
              <span className={`flex-1 min-w-0 text-sm text-gray-900 ${EXAM_TEXT_CLS}`}>{text}</span>
            </button>
          );
        })}
      </div>
    );
  }

  if (TEXT_TYPES.has(qt)) {
    const isCorrect = perResult?.is_correct;
    return (
      <div>
        <input type="text"
          value={value || ''}
          onChange={(e) => onChange(q.id, { text_answer: e.target.value })}
          disabled={locked}
          placeholder="Your answer…"
          data-testid={`ielts-ls-q${q.question_number}-input`}
          className={`w-full border-2 rounded-lg px-3 py-2 text-sm outline-none ${
            perResult
              ? (isCorrect ? 'border-green-400 bg-green-50' : 'border-red-400 bg-red-50')
              : 'border-gray-200 focus:border-red-700'
          }`} />
        {perResult && !isCorrect && correct && (
          <div className="text-xs mt-1 text-red-700">
            <span className="font-semibold">Expected:</span>{' '}
            <span className={EXAM_TEXT_CLS}>{correct}</span>
          </div>
        )}
      </div>
    );
  }

  // Fallback
  return (
    <input type="text"
      value={value || ''}
      onChange={(e) => onChange(q.id, { text_answer: e.target.value })}
      disabled={locked}
      placeholder="Your answer…"
      className="w-full border-2 rounded-lg px-3 py-2 text-sm outline-none border-gray-200 focus:border-red-700" />
  );
};

const fmtTime = (s) => {
  if (!Number.isFinite(s)) return '0:00';
  const m = Math.floor(s / 60); const r = Math.floor(s % 60);
  return `${m}:${r.toString().padStart(2, '0')}`;
};

/** Minimal audio player — HTML5 native plus a transcript toggle. */
const AudioPlayer = ({ src, hideTranscript = false }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);

  const toggle = () => {
    const a = audioRef.current; if (!a) return;
    if (a.paused) { a.play(); setPlaying(true); }
    else { a.pause(); setPlaying(false); }
  };
  const restart = () => {
    const a = audioRef.current; if (!a) return;
    a.currentTime = 0; a.play(); setPlaying(true);
  };
  const seek = (e) => {
    const a = audioRef.current; if (!a || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    a.currentTime = ((e.clientX - rect.left) / rect.width) * duration;
  };

  if (!src) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-500 flex items-center gap-2">
        <Volume2 className="h-4 w-4" />
        <span>No audio URL provided yet — use the transcript below to practice the section content.</span>
      </div>
    );
  }
  return (
    <div className="rounded-xl bg-black text-white p-3 flex items-center gap-3" data-testid="ielts-ls-audio-player">
      <Button onClick={toggle} variant="ghost" size="icon"
        className="text-white hover:bg-white/10 rounded-full h-9 w-9 flex-shrink-0"
        data-testid="ielts-ls-audio-toggle">
        {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
      </Button>
      <Button onClick={restart} variant="ghost" size="icon"
        className="text-white hover:bg-white/10 rounded-full h-9 w-9 flex-shrink-0">
        <RotateCcw className="h-4 w-4" />
      </Button>
      <div onClick={seek} className="flex-1 h-1.5 bg-white/20 rounded-full cursor-pointer relative" data-testid="ielts-ls-audio-progress">
        <div className="absolute inset-y-0 left-0 bg-[#D3D86F] rounded-full"
          style={{ width: duration ? `${(current / duration) * 100}%` : '0%' }} />
      </div>
      <div className="text-xs font-mono tabular-nums text-white/80 flex-shrink-0">
        {fmtTime(current)} / {fmtTime(duration)}
      </div>
      <audio ref={audioRef}
        src={src}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
        onTimeUpdate={(e) => setCurrent(e.currentTarget.currentTime)}
        onEnded={() => setPlaying(false)}
        preload="metadata" />
    </div>
  );
};

const IeltsListeningExam = ({
  listeningSet,
  questions = [],
  answers = {},
  results = null,
  onChangeAnswer,
  locked = false,
  hideTranscriptDuringExam = false,  // mock-mode: hide transcript by default
}) => {
  const groups = useMemo(() => groupQuestions(questions), [questions]);
  const perResultMap = useMemo(() => {
    const m = {};
    if (results?.results) for (const r of results.results) m[r.question_id] = r;
    return m;
  }, [results]);
  const [transcriptOpen, setTranscriptOpen] = useState(!hideTranscriptDuringExam);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5" data-testid="ielts-listening-exam">
      {/* LEFT — audio + transcript */}
      <Card className="border-gray-200" data-testid="ielts-listening-audio">
        <CardContent className="p-5 sm:p-6 lg:p-7">
          <div className="text-[11px] uppercase tracking-[0.2em] text-red-700 font-semibold mb-1.5">
            Listening · {(listeningSet?.section_number || 'section_1').replace('_', ' ')}
          </div>
          <h2 className={`text-xl sm:text-2xl font-bold tracking-tight mb-3 ${EXAM_TEXT_CLS}`}>
            {listeningSet?.topic || 'Listening section'}{listeningSet?.subtopic ? ` — ${listeningSet.subtopic}` : ''}
          </h2>
          <div className="flex flex-wrap gap-1.5 mb-4">
            {listeningSet?.module && <Badge variant="outline" className="text-[10px] uppercase">{listeningSet.module}</Badge>}
            {listeningSet?.difficulty && <Badge variant="outline" className="text-[10px] capitalize">{listeningSet.difficulty}</Badge>}
            {listeningSet?.section_number && <Badge variant="outline" className="text-[10px] uppercase">{listeningSet.section_number.replace('_', ' ')}</Badge>}
          </div>

          <AudioPlayer src={listeningSet?.audio_url} />

          {listeningSet?.image_url && (
            <div className="mt-4 rounded-lg overflow-hidden border border-gray-200">
              <img src={listeningSet.image_url} alt="Map / diagram" className="w-full h-auto" />
            </div>
          )}

          {listeningSet?.instructions && (
            <div className="mt-4 text-sm bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-900">
              <span className="font-semibold">Instructions: </span>
              <span className={EXAM_TEXT_CLS}>{listeningSet.instructions}</span>
            </div>
          )}

          {listeningSet?.transcript_text ? (
            <div className="mt-4">
              <button
                type="button"
                onClick={() => setTranscriptOpen((x) => !x)}
                className="text-xs font-semibold text-red-700 hover:text-red-800 flex items-center gap-1.5"
                data-testid="ielts-ls-transcript-toggle">
                {transcriptOpen ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                {transcriptOpen ? 'Hide transcript' : 'Show transcript'}
              </button>
              {transcriptOpen && (
                <div
                  className={`mt-2 prose prose-sm max-w-none text-gray-900 lg:max-h-[55vh] lg:overflow-y-auto pr-1 ${EXAM_TEXT_CLS}`}
                  style={{ fontFamily: 'Georgia, serif', lineHeight: 1.7 }}
                  data-testid="ielts-listening-transcript">
                  <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-gray-500 mb-2">
                    <FileText className="h-3.5 w-3.5" /> Transcript
                  </div>
                  {(listeningSet.transcript_text || '').split(/\n{2,}/).map((para, i) => (
                    <p key={i} className="mb-3">{para}</p>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="mt-4 text-xs text-gray-500 italic">No transcript available for this set.</div>
          )}
        </CardContent>
      </Card>

      {/* RIGHT — questions */}
      <Card className="border-gray-200" data-testid="ielts-listening-questions">
        <CardContent className="p-5 sm:p-6 lg:p-7">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-red-700 font-semibold mb-1.5">
                Questions
              </div>
              <h3 className="text-xl sm:text-2xl font-bold tracking-tight">
                {questions.length} question{questions.length === 1 ? '' : 's'}
              </h3>
            </div>
          </div>

          <div className="lg:max-h-[72vh] lg:overflow-y-auto pr-1 space-y-6">
            {groups.map((g, gi) => {
              const first = g.items[0]?.question_number;
              const last = g.items[g.items.length - 1]?.question_number;
              const range = first && last && first !== last ? `${first}–${last}` : `${first ?? ''}`;
              return (
                <div key={gi} data-testid={`ielts-ls-group-${gi}`}>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3">
                    <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-1">
                      Questions {range} · {QTYPE_LABEL[g.question_type] || g.question_type.replace(/_/g, ' ')}
                    </div>
                    {g.instructions && (
                      <div className={`text-sm text-gray-800 ${EXAM_TEXT_CLS}`}>{g.instructions}</div>
                    )}
                  </div>
                  <div className="space-y-5">
                    {g.items.map((q) => {
                      const ans = answers[q.id] || {};
                      const val = ans.selected_option ?? ans.text_answer ?? '';
                      const pr = perResultMap[q.id] || null;
                      return (
                        <div key={q.id} data-testid={`ielts-ls-q-${q.question_number}`}>
                          <div className="flex items-start gap-2.5 mb-1.5">
                            <span className="flex-shrink-0 mt-0.5 w-7 h-7 rounded-md bg-black text-white flex items-center justify-center text-xs font-bold">
                              {q.question_number}
                            </span>
                            <p className={`flex-1 text-sm sm:text-[15px] text-gray-900 ${EXAM_TEXT_CLS}`}>
                              {q.question_text}
                            </p>
                          </div>
                          <div style={{ paddingLeft: 38 }}>
                            <QuestionItem
                              q={q}
                              value={val}
                              locked={locked || !!pr}
                              perResult={pr}
                              onChange={onChangeAnswer} />
                            {pr?.explanation && (
                              <div className="mt-2 text-xs text-gray-600 bg-amber-50 border border-amber-200 rounded p-2">
                                <span className="font-semibold text-amber-900">Explanation:</span>{' '}
                                <span className={EXAM_TEXT_CLS}>{pr.explanation}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {!questions.length && (
              <div className="text-sm text-gray-500 text-center py-10">
                No questions linked to this listening set yet.
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default IeltsListeningExam;
